'use client';

import { Link } from 'react-router';
import { ArrowUpRight, ShoppingBag, Sparkles } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import type { MerchProduct } from '../interfaces/merch';
import { MERCH_CATEGORIES } from '../interfaces/merch';

interface MerchProductCardProps {
  product: MerchProduct;
  storeSlug: string;
  currency?: string;
  featured?: boolean;
}

function formatPrice(value: number, currency = 'CLP') {
  return new Intl.NumberFormat('es-CL', { style: 'currency', currency }).format(value);
}

function getCategoryLabel(category?: string) {
  return MERCH_CATEGORIES.find((c) => c.value === category)?.label;
}

export function MerchProductCard({
  product,
  storeSlug,
  currency = 'CLP',
  featured = false,
}: MerchProductCardProps) {
  const outOfStock = product.stock_quantity <= 0;
  const primaryImage =
    product.images?.find((img) => img.is_primary)?.url ?? product.image_url;
  const availableSizes = (product.variants ?? []).filter(
    (v) => v.is_available && v.stock_quantity > 0,
  );
  const detailHref = `/merch/${storeSlug}/${product.id}`;

  return (
    <Card
      variant="glass"
      className={`group overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-[color-mix(in_srgb,var(--merch-accent)_12%,transparent)] ${
        featured
          ? 'ring-1 ring-[color-mix(in_srgb,var(--merch-accent)_35%,transparent)] sm:col-span-2 lg:col-span-2 xl:col-span-2'
          : 'hover:border-[color-mix(in_srgb,var(--merch-accent)_30%,transparent)]'
      }`}
    >
      <Link to={detailHref} className="block">
        <div
          className={`relative bg-muted/20 ${featured ? 'aspect-[16/10] sm:aspect-[2/1]' : 'aspect-square'}`}
        >
          {primaryImage ? (
            <img 
              src={primaryImage} 
              alt={product.name} 
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" 
            />
          ) : (
            <div className="flex h-full items-center justify-center">
              <ShoppingBag className="h-12 w-12 text-muted-foreground/40" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 transition-opacity group-hover:opacity-100" />

          {featured && !outOfStock && (
            <Badge className="absolute left-3 top-3 gap-1 bg-[var(--merch-accent)] text-white border-0">
              <Sparkles className="h-3 w-3" />
              Destacado
            </Badge>
          )}
          {outOfStock && (
            <Badge className="absolute right-3 top-3 bg-destructive/90">Agotado</Badge>
          )}
        </div>
      </Link>

      <CardHeader className={`pb-2 ${featured ? 'sm:flex sm:items-start sm:justify-between sm:gap-4' : ''}`}>
        <div>
          {product.details?.category && (
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              {getCategoryLabel(product.details.category)}
            </p>
          )}
          <CardTitle className={featured ? 'text-xl sm:text-2xl' : 'text-lg'}>
            <Link to={detailHref} className="transition-colors hover:text-[var(--merch-accent)]">
              {product.name}
            </Link>
          </CardTitle>
          {product.description && (
            <p
              className={`text-sm text-muted-foreground mt-1 ${featured ? 'line-clamp-3' : 'line-clamp-2'}`}
            >
              {product.description}
            </p>
          )}
        </div>
        <p
          className={`font-bold text-[var(--merch-accent)] ${featured ? 'text-3xl sm:text-right shrink-0' : 'text-2xl mt-2 sm:mt-0'}`}
        >
          {formatPrice(Number(product.price), currency)}
        </p>
      </CardHeader>

      <CardContent className="space-y-3 pt-0">
        {availableSizes.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {availableSizes.slice(0, 6).map((variant) => (
              <Badge key={variant.id} variant="outline" className="text-[10px] px-2 py-0">
                {variant.size}
              </Badge>
            ))}
          </div>
        )}
        {!outOfStock && availableSizes.length === 0 && (
          <p className="text-xs text-muted-foreground">{product.stock_quantity} en stock</p>
        )}
      </CardContent>

      <CardFooter>
        <Button
          className="w-full gap-2 bg-[var(--merch-accent)] text-white hover:bg-[color-mix(in_srgb,var(--merch-accent)_85%,black)]"
          disabled={outOfStock}
          asChild
        >
          <Link to={detailHref}>
            {outOfStock ? 'Sin stock' : 'Ver producto'}
            {!outOfStock && <ArrowUpRight className="h-4 w-4" />}
          </Link>
        </Button>
      </CardFooter>
    </Card>
  );
}
